import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, CheckCircle, Lock, LayoutDashboard, Settings, LogOut, ChevronDown, Plus, User, Crown, Calendar, MapPin, Clock, ChevronRight, ListTodo, ShoppingCart, FileText, BookOpen, Tv, Car, Image, Map, Contact, Wallet, Network, Sparkles, Heart, GraduationCap, Book, Hand, Building, Plane, ScrollText, Dog, X } from 'lucide-react';
import { fetchFamilies } from '../services/familiesApi';
import { getCurrentUserData } from '../utils/auth';
import apiClient from '../utils/apiClient';
import { getUserInitials, getFamilyInitials } from '../utils/userUtils';
import Premium from './Premium';

const Dashboard = () => {
    const navigate = useNavigate();
    const [showFamilyDropdown, setShowFamilyDropdown] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [families, setFamilies] = useState([]);
    const [selectedFamily, setSelectedFamily] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [categoryOrder, setCategoryOrder] = useState(null); // Custom category order from backend
    const dropdownRef = useRef(null);
    const userMenuRef = useRef(null);

    // Category counts
    const [counts, setCounts] = useState({
        calendar: 0,
        todos: 0,
        secrets: 0,
        documents: 0,
        appliances: 0,
        vehicles: 0,
        trips: 0
    });

    // TODO items
    const [todoItems, setTodoItems] = useState([]);
    const [shoppingItems, setShoppingItems] = useState([]);
    const [loadingTodos, setLoadingTodos] = useState(false);

    // Calendar events
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [loadingCalendar, setLoadingCalendar] = useState(false);

    // All available categories (will be sorted alphabetically by default or by custom order)
    const allCategories = [
        {
            id: 'appliances',
            name: 'Appliances & Devices',
            description: 'Track service dates, warranties, and insurance',
            icon: Tv,
            iconColor: 'indigo-500',
            route: '/appliances',
            countKey: 'appliances'
        },
        {
            id: 'beliefs',
            name: 'Beliefs & Traditions',
            description: 'Preserve family values, customs, and rituals',
            icon: Sparkles,
            iconColor: 'purple-600',
            route: '/beliefs',
            countKey: null
        },
        {
            id: 'calendar',
            name: 'Calendar',
            description: 'Shared family calendar for events and activities',
            icon: Calendar,
            iconColor: 'violet-500',
            route: '/calendar',
            countKey: 'calendar'
        },
        {
            id: 'community',
            name: 'Community & Giving',
            description: 'Track donations, volunteering, and social impact',
            icon: Hand,
            iconColor: 'fuchsia-500',
            route: '/community',
            countKey: null
        },
        {
            id: 'contacts',
            name: 'Contact Book',
            description: 'Store contacts for each family member',
            icon: Contact,
            iconColor: 'cyan-500',
            route: '/contacts',
            countKey: null
        },
        {
            id: 'documents',
            name: 'Documents',
            description: 'Store and manage important family documents',
            icon: FileText,
            iconColor: 'blue-500',
            route: '/documents',
            countKey: 'documents'
        },
        {
            id: 'education',
            name: 'Education & Learning',
            description: 'Track growth, progress, and achievements',
            icon: GraduationCap,
            iconColor: 'sky-500',
            route: '/education',
            countKey: null
        },
        {
            id: 'familytree',
            name: 'Family Tree',
            description: 'Visualize your complete family tree and relationships',
            icon: Network,
            iconColor: 'orange-500',
            route: '/familytree',
            countKey: null
        },
        {
            id: 'finance',
            name: 'Finance',
            description: 'Track income, expenses, and budgets',
            icon: Wallet,
            iconColor: 'emerald-500',
            route: '/finance',
            countKey: null
        },
        {
            id: 'health',
            name: 'Health & Wellness',
            description: 'Centralize family health and wellness data',
            icon: Heart,
            iconColor: 'rose-500',
            route: '/healthwellness',
            countKey: null
        },
        {
            id: 'legacy',
            name: 'Legacy & Estate',
            description: 'Long-term family continuity and estate planning',
            icon: ScrollText,
            iconColor: 'stone-600',
            route: '/legacy',
            countKey: null
        },
        {
            id: 'pets',
            name: 'Pets',
            description: 'Vet records, feeding schedules, and pet care',
            icon: Dog,
            iconColor: 'orange-600',
            route: '/pets',
            countKey: null
        },
        {
            id: 'recipes',
            name: 'Recipe Box',
            description: 'Save and share your favorite family recipes',
            icon: BookOpen,
            iconColor: 'amber-500',
            route: '/recipes',
            countKey: null
        },
        {
            id: 'secrets',
            name: 'Secrets Vault',
            description: 'Secure storage for passwords and important info',
            icon: Lock,
            iconColor: 'red-500',
            route: '/secretsvault',
            countKey: 'secrets'
        },
        {
            id: 'todos',
            name: 'To-Do Lists',
            description: 'Shared tasks and chores for the whole family',
            icon: CheckCircle,
            iconColor: 'primary-green',
            route: '/todolists',
            countKey: 'todos'
        },
        {
            id: 'trips',
            name: 'Travel & Vacations',
            description: 'Plan trips with itineraries and packing lists',
            icon: Plane,
            iconColor: 'sky-600',
            route: '/trips',
            countKey: 'trips'
        },
        {
            id: 'vehicles',
            name: 'Vehicles',
            description: 'Manage vehicles, licenses, and related details',
            icon: Car,
            iconColor: 'slate-600',
            route: '/vehicles',
            countKey: 'vehicles'
        }
    ];

    // Get sorted categories (custom order or alphabetical)
    const getSortedCategories = () => {
        if (categoryOrder && Array.isArray(categoryOrder)) {
            // Use custom order
            const ordered = [];
            categoryOrder.forEach(id => {
                const category = allCategories.find(cat => cat.id === id);
                if (category) ordered.push(category);
            });
            // Add any new categories not in custom order (alphabetically)
            const remainingCategories = allCategories
                .filter(cat => !categoryOrder.includes(cat.id))
                .sort((a, b) => a.name.localeCompare(b.name));
            return [...ordered, ...remainingCategories];
        }
        // Default alphabetical sorting
        return [...allCategories].sort((a, b) => a.name.localeCompare(b.name));
    };

    useEffect(() => {
        const loadFamilies = async () => {
            try {
                // Load families from API
                const families = await fetchFamilies();
                setFamilies(families);

                // Load selected family from localStorage (just the ID)
                const selectedFamilyId = localStorage.getItem('selectedFamilyId');
                if (selectedFamilyId && families.length > 0) {
                    const family = families.find(f => f.id === selectedFamilyId);
                    setSelectedFamily(family);
                }
            } catch (error) {
                console.error('Error loading families:', error);
            }
        };

        loadFamilies();
    }, []);

    useEffect(() => {
        // Load current user information from localStorage first (for quick display)
        const userData = getCurrentUserData();
        if (userData) {
            setCurrentUser(userData);
        }
        
        // Then refresh from API to ensure we have the latest data (including picture)
        const refreshUserData = async () => {
            try {
                const freshUserData = await apiClient.getCurrentUser();
                localStorage.setItem('currentUser', JSON.stringify(freshUserData));
                setCurrentUser(freshUserData);
            } catch (error) {
                console.error('Error refreshing user data:', error);
            }
        };
        
        refreshUserData();
    }, []);

    useEffect(() => {
        // Close dropdowns when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowFamilyDropdown(false);
            }
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (showPremiumModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showPremiumModal]);

    useEffect(() => {
        // Load counts for all categories from API
        const loadCounts = async () => {
            if (!selectedFamily) return;

            try {
                const countsData = await apiClient.request(`/v1/families/${selectedFamily.id}/counts`);
                setCounts({
                    calendar: countsData.calendar || 0,
                    todos: countsData.todos || 0,
                    secrets: countsData.secrets || 0,
                    documents: countsData.documents || 0,
                    appliances: countsData.appliances || 0,
                    vehicles: countsData.vehicles || 0,
                    trips: countsData.trips || 0
                });
            } catch (error) {
                // Fallback to individual requests if batch endpoint fails
                try {
                    const [calendarData, todos, secrets, documents, appliances, vehicles, trips] = await Promise.all([
                        apiClient.request(`/v1/families/${selectedFamily.id}/calendar_events`).catch(() => ({ items: [], total: 0 })),
                        apiClient.request(`/v1/families/${selectedFamily.id}/todos`).catch(() => []),
                        apiClient.request(`/v1/families/${selectedFamily.id}/secrets`).catch(() => []),
                        apiClient.request(`/v1/families/${selectedFamily.id}/documents`).catch(() => []),
                        apiClient.request(`/v1/families/${selectedFamily.id}/appliances`).catch(() => []),
                        apiClient.request(`/v1/families/${selectedFamily.id}/vehicles`).catch(() => []),
                        apiClient.request(`/v1/families/${selectedFamily.id}/trips`).catch(() => [])
                    ]);
                    setCounts({
                        calendar: calendarData?.total ?? (Array.isArray(calendarData?.items) ? calendarData.items.length : 0),
                        todos: Array.isArray(todos) ? todos.length : 0,
                        secrets: Array.isArray(secrets) ? secrets.length : 0,
                        documents: Array.isArray(documents) ? documents.length : 0,
                        appliances: Array.isArray(appliances) ? appliances.length : 0,
                        vehicles: Array.isArray(vehicles) ? vehicles.length : 0,
                        trips: Array.isArray(trips) ? trips.length : 0
                    });
                } catch (fallbackError) {
                    console.error('Error loading counts:', fallbackError);
                }
            }
        };

        loadCounts();

        // Reload counts when window gains focus (in case data changed)
        window.addEventListener('focus', loadCounts);
        return () => window.removeEventListener('focus', loadCounts);
    }, [selectedFamily]);

    useEffect(() => {
        // Load category order for selected family
        const loadCategoryOrder = async () => {
            if (!selectedFamily) return;

            try {
                const data = await apiClient.request(`/v1/families/${selectedFamily.id}/category-order`);
                setCategoryOrder(data.category_order);
            } catch (error) {
                console.error('Error loading category order:', error);
                setCategoryOrder(null);
            }
        };

        loadCategoryOrder();
    }, [selectedFamily]);

    useEffect(() => {
        // Load TODO items for selected family
        const loadTodoItems = async () => {
            if (!selectedFamily) return;

            try {
                setLoadingTodos(true);
                const [todosData, shoppingData] = await Promise.all([
                    apiClient.request(`/v1/families/${selectedFamily.id}/todos?category=todos&completed=false&limit=3`),
                    apiClient.request(`/v1/families/${selectedFamily.id}/todos?category=shopping&completed=false&limit=3`)
                ]);
                setTodoItems(todosData?.items || []);
                setShoppingItems(shoppingData?.items || []);
            } catch (error) {
                console.error('Error loading TODO items:', error);
            } finally {
                setLoadingTodos(false);
            }
        };

        loadTodoItems();

        // Reload todos when window gains focus
        window.addEventListener('focus', loadTodoItems);
        return () => window.removeEventListener('focus', loadTodoItems);
    }, [selectedFamily]);

    useEffect(() => {
        // Load upcoming calendar events for selected family
        const loadCalendarEvents = async () => {
            if (!selectedFamily) return;

            try {
                setLoadingCalendar(true);
                
                // Get today's date and next 7 days
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeek = new Date(today);
                nextWeek.setDate(today.getDate() + 7);
                
                // Fetch upcoming events (next 7 days, limit to 5 for dashboard)
                const eventsData = await apiClient.request(
                    `/v1/families/${selectedFamily.id}/calendar_events?limit=5`
                );
                
                // Filter events that are today or in the future
                const upcomingEvents = (eventsData.items || []).filter(event => {
                    const eventDate = new Date(event.start_date);
                    eventDate.setHours(0, 0, 0, 0);
                    return eventDate >= today;
                }).sort((a, b) => {
                    // Sort by start_date, then by start_time
                    const dateA = new Date(a.start_date);
                    const dateB = new Date(b.start_date);
                    if (dateA.getTime() !== dateB.getTime()) {
                        return dateA - dateB;
                    }
                    if (a.start_time && b.start_time) {
                        return a.start_time.localeCompare(b.start_time);
                    }
                    return 0;
                }).slice(0, 5); // Limit to 5 events
                
                setCalendarEvents(upcomingEvents);
            } catch (error) {
                console.error('Error loading calendar events:', error);
            } finally {
                setLoadingCalendar(false);
            }
        };

        loadCalendarEvents();

        // Reload events when window gains focus
        window.addEventListener('focus', loadCalendarEvents);
        return () => window.removeEventListener('focus', loadCalendarEvents);
    }, [selectedFamily]);

    const handleLogout = () => {
        localStorage.removeItem('hasLoggedIn');
        navigate('/login');
    };

    // Render a category card
    const renderCategoryCard = (category) => {
        const IconComponent = category.icon;
        const count = category.countKey ? counts[category.countKey] : null;
        // Make icons transparent for Documents and To-Do Lists
        const isTransparentIcon = category.id === 'documents' || category.id === 'todos';
        
        return (
            <div
                key={category.id}
                onClick={() => navigate(category.route)}
                className="bg-white/90 backdrop-blur-sm rounded-xl shadow-md hover:shadow-xl transition-shadow duration-200 p-4 cursor-pointer relative"
            >
                <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center gap-2 flex-shrink-0" style={{ width: '32%' }}>
                        <div className={`${isTransparentIcon ? 'bg-transparent' : `bg-${category.iconColor}/10`} p-3 rounded-2xl w-full`} style={{ aspectRatio: '1' }}>
                            <IconComponent className={`w-full h-full text-${category.iconColor}`} />
                        </div>
                        {count > 0 && (
                            <span className="text-xs font-semibold text-gray-700">
                                {count} {count === 1 ? category.name.slice(0, -1) : category.name}
                            </span>
                        )}
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-primary-blue mb-2">{category.name}</h3>
                        <p className="text-sm text-gray-600">{category.description}</p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen relative">
            {/* Gradient Background - Matching Landing Page */}
            <div
                className="fixed inset-0 z-0"
                style={{
                    background: 'linear-gradient(135deg, rgb(59, 130, 246) 0%, rgb(236, 72, 153) 100%)'
                }}
            />
            
            {/* Header - Fixed Navbar */}
            <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md shadow-sm z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                            >
                                <img src="/logo.png" alt="mifamli logo" className="h-10 w-10" />
                                <span className="text-xl font-bold text-primary-blue">mifamli</span>
                            </button>

                            {/* Mi Famlis Dropdown */}
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowFamilyDropdown(!showFamilyDropdown)}
                                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-white shadow-md hover:shadow-lg transition-all backdrop-blur-md border-2 border-white/30"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(236, 72, 153, 0.9) 100%)'
                                    }}
                                >
                                    {selectedFamily ? (
                                        <>
                                            {selectedFamily.photo ? (
                                                <img
                                                    src={selectedFamily.photo}
                                                    alt={selectedFamily.name}
                                                    className="w-6 h-6 rounded-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                                                    {getFamilyInitials(selectedFamily.name)}
                                                </div>
                                            )}
                                            <span>{selectedFamily.name}</span>
                                        </>
                                    ) : (
                                        <span>Mi Famlis</span>
                                    )}
                                    <ChevronDown className={`w-4 h-4 transition-transform ${showFamilyDropdown ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Dropdown Menu */}
                                {showFamilyDropdown && (
                                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        {families.length > 0 ? (
                                            <>
                                                {families.map((family) => (
                                                    <button
                                                        key={family.id}
                                                        onClick={() => {
                                                            setSelectedFamily(family);
                                                            localStorage.setItem('selectedFamilyId', family.id.toString());
                                                            setShowFamilyDropdown(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 ${selectedFamily?.id === family.id ? 'bg-blue-50' : ''
                                                            }`}
                                                    >
                                                        {family.photo ? (
                                                            <img
                                                                src={family.photo}
                                                                alt={family.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md">
                                                                {getFamilyInitials(family.name)}
                                                            </div>
                                                        )}
                                                        <div className="flex-1">
                                                            <p className="font-semibold text-gray-800">{family.name}</p>
                                                            {family.tagline && (
                                                                <p className="text-xs text-gray-500 italic truncate">"{family.tagline}"</p>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                                <div className="border-t border-gray-200 my-2"></div>
                                            </>
                                        ) : (
                                            <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                No families yet
                                            </div>
                                        )}
                                        <button
                                            onClick={() => {
                                                setShowFamilyDropdown(false);
                                                navigate('/famlis');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-primary-blue/5 flex items-center gap-3 text-primary-blue font-semibold"
                                        >
                                            <Plus className="w-5 h-5" />
                                            <span>Add Family</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-4">
                            {/* Premium Button */}
                            <div className="relative">
                                <button
                                    onClick={() => setShowPremiumModal(true)}
                                    className="relative px-4 py-2 rounded-lg font-semibold text-white hover:scale-105 transform transition-all duration-200 backdrop-blur-md border-2 border-white/50 overflow-hidden group"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.85) 0%, rgba(236, 72, 153, 0.85) 100%)',
                                        boxShadow: '0 0 20px rgba(59, 130, 246, 0.6), 0 0 40px rgba(236, 72, 153, 0.6)'
                                    }}
                                >
                                    <span className="relative z-10 flex items-center gap-2">
                                        <span className="hidden sm:inline">Go Premium</span>
                                        <span className="sm:hidden">Premium</span>
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500/50 to-pink-500/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                                </button>
                                
                                {/* Golden Crown Overlay */}
                                <div className="absolute -top-1 -right-1 transform rotate-12">
                                    <Crown className="w-5 h-5 text-yellow-500 fill-yellow-400 drop-shadow-lg" />
                                </div>
                            </div>

                            {/* Family Settings Button */}
                            {selectedFamily && (
                                <button
                                    onClick={() => navigate('/settings')}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                    title="Family Settings"
                                >
                                    <Settings className="w-6 h-6 text-gray-600" />
                                </button>
                            )}

                            {/* User Avatar Menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu(!showUserMenu)}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                >
                                    {currentUser?.picture ? (
                                        <img 
                                            src={currentUser.picture} 
                                            alt={currentUser.name || 'User'} 
                                            className="w-10 h-10 rounded-full object-cover shadow-md hover:shadow-lg transition-shadow border-2 border-white"
                                        />
                                    ) : (
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg shadow-md hover:shadow-lg transition-shadow border-2 border-white">
                                            {getUserInitials(currentUser?.name)}
                                        </div>
                                    )}
                                </button>

                                {/* User Dropdown Menu */}
                                {showUserMenu && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-200">
                                            <p className="text-sm font-semibold text-gray-800">{currentUser?.name || 'User Account'}</p>
                                            <p className="text-xs text-gray-500 truncate">{currentUser?.email || 'Manage your profile'}</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                alert('Profile settings coming soon!');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <User className="w-5 h-5 text-gray-600" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                navigate('/settings');
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                                        >
                                            <Settings className="w-5 h-5 text-gray-600" />
                                            <span>Family Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 my-2"></div>
                                        <button
                                            onClick={() => {
                                                setShowUserMenu(false);
                                                handleLogout();
                                            }}
                                            className="w-full px-4 py-3 text-left hover:bg-red-50 flex items-center gap-3 text-red-600 font-semibold"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-20">
                {/* Overview Section */}
                <div className="mb-12">
                    <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6 drop-shadow-lg">Overview</h2>
                    <div className="overflow-x-auto pb-4">
                        <div className="flex gap-6 min-w-max">
                            {/* Events Card */}
                            <div 
                                onClick={() => navigate('/calendar')}
                                className="w-96 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 flex-shrink-0 relative overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold text-primary-blue mb-6">Upcoming Events</h3>
                                    {loadingCalendar ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                                        </div>
                                    ) : calendarEvents.length > 0 ? (
                                        <div className="space-y-4">
                                            {calendarEvents.map((event) => {
                                                const eventDate = new Date(event.start_date);
                                                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                                const dayName = days[eventDate.getDay()];
                                                const dayNumber = eventDate.getDate();
                                                const monthName = months[eventDate.getMonth()];
                                                
                                                return (
                                                    <div key={event.id} className="border-l-4 pl-4" style={{ borderColor: event.color || '#3b82f6' }}>
                                                        <h4 className="text-gray-900 font-bold text-lg mb-1 truncate">{event.title}</h4>
                                                        {event.location && (
                                                            <div className="flex items-start gap-2 text-gray-700 text-sm mb-1">
                                                                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                                <span className="truncate">{event.location}</span>
                                                            </div>
                                                        )}
                                                        <div className="flex items-center gap-2 text-gray-700 text-sm">
                                                            <Clock className="w-3 h-3 flex-shrink-0" />
                                                            <span>
                                                                {event.all_day 
                                                                    ? 'All day' 
                                                                    : `${event.start_time || ''}${event.end_time ? ` - ${event.end_time}` : ''}`
                                                                }
                                                            </span>
                                                        </div>
                                                        <div className="text-gray-600 text-xs mt-1">
                                                            {dayName} {dayNumber} {monthName}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-gray-600 text-center py-8">
                                            <p className="text-lg">No upcoming events</p>
                                            <p className="text-sm mt-2">Click to add an event</p>
                                        </div>
                                    )}
                                    {calendarEvents.length > 0 && (
                                        <div className="mt-6 text-primary-blue text-lg font-bold hover:text-primary-blue/80 transition-colors">
                                            View all →
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* To-Do Card */}
                            <div 
                                onClick={() => navigate('/todolists?tab=todos')}
                                className="w-96 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 min-h-[280px] flex-shrink-0 relative overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold text-primary-blue mb-6">To-Do</h3>
                                    {loadingTodos ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-blue"></div>
                                        </div>
                                    ) : todoItems.length > 0 ? (
                                        <div className="space-y-4">
                                            {todoItems.map((todo) => (
                                                <div key={todo.id} className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={todo.completed}
                                                        readOnly
                                                        className="w-6 h-6 rounded border-2 border-gray-300 bg-white cursor-pointer"
                                                    />
                                                    <span className="text-gray-900 text-lg font-medium truncate">
                                                        {todo.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-600 text-center py-8">
                                            <p className="text-lg">No pending tasks</p>
                                            <p className="text-sm mt-2">Click to add a new task</p>
                                        </div>
                                    )}
                                    {todoItems.length > 0 && (
                                        <div 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/todolists?tab=todos');
                                            }}
                                            className="mt-6 text-primary-blue text-lg font-bold hover:text-primary-blue/80 transition-colors cursor-pointer"
                                        >
                                            View all →
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Shopping List Card */}
                            <div 
                                onClick={() => navigate('/todolists?tab=shopping')}
                                className="w-96 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 min-h-[280px] flex-shrink-0 relative overflow-hidden cursor-pointer hover:shadow-2xl transition-shadow"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-2xl font-bold text-primary-blue mb-6">Shopping List</h3>
                                    {loadingTodos ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-blue"></div>
                                        </div>
                                    ) : shoppingItems.length > 0 ? (
                                        <div className="space-y-4">
                                            {shoppingItems.map((item) => (
                                                <div key={item.id} className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={item.completed}
                                                        readOnly
                                                        className="w-6 h-6 rounded border-2 border-gray-300 bg-white cursor-pointer"
                                                    />
                                                    <span className="text-gray-900 text-lg font-medium truncate">
                                                        {item.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-gray-600 text-center py-8">
                                            <p className="text-lg">No shopping items</p>
                                            <p className="text-sm mt-2">Click to add items</p>
                                        </div>
                                    )}
                                    {shoppingItems.length > 0 && (
                                        <div 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate('/todolists?tab=shopping');
                                            }}
                                            className="mt-6 text-primary-blue text-lg font-bold hover:text-primary-blue/80 transition-colors cursor-pointer"
                                        >
                                            View all →
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Categories Section Title */}
                <div className="mb-6">
                    <h2 className="text-4xl sm:text-5xl font-bold text-white drop-shadow-lg">Categories</h2>
                </div>

                {/* Dashboard Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getSortedCategories().map(category => renderCategoryCard(category))}
                </div>
            </div>

            {/* Premium Modal - MOVED HERE */}
            {showPremiumModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black bg-opacity-75">
                    <div className="relative rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" style={{ background: 'transparent' }}>
                        {/* Close Button */}
                        <button
                            onClick={() => setShowPremiumModal(false)}
                            className="absolute top-4 right-4 z-50 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-colors"
                        >
                            <X className="w-6 h-6 text-gray-600" />
                        </button>
                        
                        {/* Premium Component */}
                        <Premium isModal={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
